import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, Users, Send } from 'lucide-react';
import { toast } from 'sonner';

const AsignarActividad = () => {
  const navigate = useNavigate();
  const { actividadId } = useParams();
  
  const [actividad, setActividad] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [asignacionData, setAsignacionData] = useState({
    grupo_id: '',
    estudiante_ids: [],
    fecha_apertura: '',
    fecha_cierre: ''
  });
  
  const [modoAsignacion, setModoAsignacion] = useState('grupo'); // 'grupo' o 'individual'

  const fetchData = useCallback(async () => {
    try {
      const [actividadRes, gruposRes, usuariosRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/actividades`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/grupos`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, { withCredentials: true })
      ]);
      
      const actividadEncontrada = actividadRes.data.find(a => a.actividad_id === actividadId);
      setActividad(actividadEncontrada);
      setGrupos(gruposRes.data);
      setUsuarios(usuariosRes.data.filter(u => u.roles.includes('estudiante')));
      
      // Set default dates
      const hoy = new Date();
      const unaSemana = new Date();
      unaSemana.setDate(unaSemana.getDate() + 7);
      
      setAsignacionData(prev => ({
        ...prev,
        fecha_apertura: hoy.toISOString().split('T')[0],
        fecha_cierre: unaSemana.toISOString().split('T')[0]
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [actividadId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEstudianteToggle = (userId) => {
    setAsignacionData(prev => ({
      ...prev,
      estudiante_ids: prev.estudiante_ids.includes(userId)
        ? prev.estudiante_ids.filter(id => id !== userId)
        : [...prev.estudiante_ids, userId]
    }));
  };

  const handleAsignar = async () => {
    if (modoAsignacion === 'grupo' && !asignacionData.grupo_id) {
      toast.error('Selecciona un grupo');
      return;
    }
    
    if (modoAsignacion === 'individual' && asignacionData.estudiante_ids.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    
    if (!asignacionData.fecha_apertura || !asignacionData.fecha_cierre) {
      toast.error('Define las fechas de apertura y cierre');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/asignaciones`,
        {
          actividad_id: actividadId,
          grupo_id: modoAsignacion === 'grupo' ? asignacionData.grupo_id : null,
          estudiante_ids: modoAsignacion === 'individual' ? asignacionData.estudiante_ids : [],
          fecha_apertura: asignacionData.fecha_apertura,
          fecha_cierre: asignacionData.fecha_cierre
        },
        { withCredentials: true }
      );
      
      toast.success('Actividad asignada exitosamente');
      navigate('/docente/actividades');
    } catch (error) {
      console.error('Error assigning actividad:', error);
      toast.error('Error al asignar actividad');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!actividad) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Actividad no encontrada</p>
            <Button onClick={() => navigate('/docente/actividades')} className="mt-4">Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/docente/actividades')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Asignar Actividad</h1>
              <p className="text-sm text-muted-foreground">{actividad.titulo}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-6">
          {/* Info de Actividad */}
          <Card>
            <CardHeader>
              <CardTitle>Información de la Actividad</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge>{actividad.tipo_actividad}</Badge>
                  <Badge variant="outline">{actividad.estado}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{actividad.descripcion}</p>
              </div>
            </CardContent>
          </Card>

          {/* Modo de Asignación */}
          <Card>
            <CardHeader>
              <CardTitle>¿Cómo deseas asignar esta actividad?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Card 
                  className={`cursor-pointer transition-all border-2 ${modoAsignacion === 'grupo' ? 'border-primary bg-primary/5' : 'border-border'}`}
                  onClick={() => setModoAsignacion('grupo')}
                  data-testid="modo-grupo"
                >
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      <CardTitle className="text-lg">Por Grupo</CardTitle>
                    </div>
                    <CardDescription>Asignar a todos los estudiantes de un grupo</CardDescription>
                  </CardHeader>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all border-2 ${modoAsignacion === 'individual' ? 'border-primary bg-primary/5' : 'border-border'}`}
                  onClick={() => setModoAsignacion('individual')}
                  data-testid="modo-individual"
                >
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      <CardTitle className="text-lg">Individual</CardTitle>
                    </div>
                    <CardDescription>Seleccionar estudiantes específicos</CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Selección de Grupo o Estudiantes */}
          {modoAsignacion === 'grupo' ? (
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Grupo</CardTitle>
              </CardHeader>
              <CardContent>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={asignacionData.grupo_id}
                  onChange={(e) => setAsignacionData({ ...asignacionData, grupo_id: e.target.value })}
                  data-testid="select-grupo"
                >
                  <option value="">Seleccionar grupo</option>
                  {grupos.map(g => (
                    <option key={g.grupo_id} value={g.grupo_id}>
                      {g.nombre} ({g.estudiante_ids?.length || 0} estudiantes)
                    </option>
                  ))}
                </select>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Estudiantes</CardTitle>
                <CardDescription>Seleccionados: {asignacionData.estudiante_ids.length}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {usuarios.map(usuario => (
                    <Label 
                      key={usuario.user_id}
                      className="flex items-center gap-2 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={asignacionData.estudiante_ids.includes(usuario.user_id)}
                        onChange={() => handleEstudianteToggle(usuario.user_id)}
                        className="h-4 w-4"
                        data-testid={`checkbox-estudiante-${usuario.user_id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{usuario.name}</p>
                        <p className="text-sm text-muted-foreground">{usuario.email}</p>
                      </div>
                    </Label>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fechas */}
          <Card>
            <CardHeader>
              <CardTitle>Programación</CardTitle>
              <CardDescription>Define cuándo estará disponible la actividad</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fecha-apertura">Fecha de Apertura</Label>
                  <Input
                    id="fecha-apertura"
                    type="date"
                    value={asignacionData.fecha_apertura}
                    onChange={(e) => setAsignacionData({ ...asignacionData, fecha_apertura: e.target.value })}
                    data-testid="input-fecha-apertura"
                  />
                </div>
                <div>
                  <Label htmlFor="fecha-cierre">Fecha de Cierre</Label>
                  <Input
                    id="fecha-cierre"
                    type="date"
                    value={asignacionData.fecha_cierre}
                    onChange={(e) => setAsignacionData({ ...asignacionData, fecha_cierre: e.target.value })}
                    data-testid="input-fecha-cierre"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botones de Acción */}
          <div className="flex gap-2 justify-end sticky bottom-4 bg-background/80 backdrop-blur-xl p-4 rounded-lg border border-border">
            <Button
              variant="outline"
              onClick={() => navigate('/docente/actividades')}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAsignar}
              data-testid="btn-asignar"
            >
              <Send className="h-4 w-4 mr-2" />
              Asignar Actividad
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AsignarActividad;
